import { ApolloClient, DataProxy, useApolloClient } from '@apollo/client';
import { Delete } from '@mui/icons-material';
import { Fab, MenuItem } from '@mui/material';
import {
  InfiniteTable,
  LockButton,
  NotificationTypeEnum,
  SearchBar,
  useDialog,
  useMenu,
  useNotification,
} from '@prismamedia/one-components';
import {
  CampaignOrderByInput,
  GetCampaignsQuery_campaigns_Campaign,
} from '__generated__/queries-newsletter';
import { deleteCampaign } from 'apollo/api';
import { useUpdateCampaignLockerId } from 'apollo/mutations/updateCampaign.newsletter.graphql';
import { useGetCampaigns } from 'apollo/queries/getCampaigns.newsletter.graphql';
import { AutomationIcon } from 'components/AutomationIcon';
import { ConfirmDialog } from 'components/ConfirmDialog';
import { FilterDrawer } from 'components/FilterDrawer';
import { SpeedDialBrands } from 'components/SpeedDialBrands';
import { ListPage, PageId } from 'pages/common/ListPage';
import { FC } from 'react';
import { useHistory } from 'react-router-dom';
import { paths, replaceParams } from 'routing';
import { auth } from 'utils/auth';
import {
  useGlobalBrandKey,
  useGlobalNewslettersFilter,
  useGlobalSearchParams,
} from 'utils/globalState';
import {
  CONFIRM_CAMPAIGN_CREATION_TEXT,
  CONFIRM_LABEL,
  DECLINE_LABEL,
  REMOVED_CAMPAIGN_CONFIRMATION_TEXT,
} from '../CampaignEditPage/AppBar/constants';

export const CampaignListPage: FC = () => {
  const history = useHistory();
  const [newslettersFilter] = useGlobalNewslettersFilter();
  const [searchParams, setSearchParams] = useGlobalSearchParams();
  const [brandKey] = useGlobalBrandKey();
  const { openMenu, closeMenu } = useMenu();
  const { openDialog, closeDialog } = useDialog();
  const { pushNotification } = useNotification();
  const updateLockerId = useUpdateCampaignLockerId();
  const brandQueryParam = brandKey ? `?brand=${brandKey}` : '';
  const client = useApolloClient() as ApolloClient<DataProxy>;

  const { data, loading, error, fetchMore, refetch } = useGetCampaigns({
    where: {
      newsletter: { id_in: newslettersFilter },
      subject_contains: searchParams?.search,
    },
    first: 100,
    orderBy: [CampaignOrderByInput.availableAt_DESC],
  });

  const deleteItem = async (campaign: GetCampaignsQuery_campaigns_Campaign) => {
    try {
      await deleteCampaign(campaign, client);
      refetch();
      pushNotification({
        message: REMOVED_CAMPAIGN_CONFIRMATION_TEXT,
        type: NotificationTypeEnum.success,
      });
    } catch (e: any) {
      pushNotification({
        message: e.message,
        type: NotificationTypeEnum.error,
      });
    }
  };

  return (
    <ListPage
      type={PageId.CAMPAIGN}
      searchBar={
        <SearchBar
          placeholder="Rechercher une campagne"
          searchParams={searchParams}
          setSearchParams={setSearchParams}
        />
      }
      filterDrawer={<FilterDrawer />}
    >
      <InfiniteTable
        list={data?.campaigns}
        loading={loading}
        error={error}
        threshold={50}
        fetchMore={({ startIndex }) =>
          fetchMore({
            variables: {
              skip: startIndex,
            },
          })
        }
        onRowClick={(_, campaign: GetCampaignsQuery_campaigns_Campaign) => {
          history.push(
            `${replaceParams(paths.CAMPAIGN_EDIT, {
              id: campaign.id,
            })}/generalities${brandQueryParam}`,
          );
        }}
        rowHoverRenderer={(campaign: GetCampaignsQuery_campaigns_Campaign) => {
          return (
            <Fab
              size="small"
              color="primary"
              onClick={(e) => {
                e.preventDefault();
                e.stopPropagation();
                openDialog(
                  <ConfirmDialog
                    title={CONFIRM_CAMPAIGN_CREATION_TEXT}
                    confirmLabel={CONFIRM_LABEL}
                    cancelLabel={DECLINE_LABEL}
                    onConfirm={() => deleteItem(campaign)}
                    closeDialog={closeDialog}
                  />,
                );
              }}
            >
              <Delete />
            </Fab>
          );
        }}
        columns={[
          {
            label: "Date d'envoi",
            key: 'availableAt',
            width: 150,
            flexShrink: 0,
          },
          {
            label: 'Template',
            key: 'newsletter',
            cellDataGetter: ({
              rowData,
            }: {
              rowData: GetCampaignsQuery_campaigns_Campaign;
            }) => rowData.newsletter?.name,
            width: 200,
          },
          {
            label: 'Objet',
            key: 'subject',
            width: 400,
            flexGrow: 1,
          },
          {
            label: 'Id',
            key: '_id',
            width: 70,
            flexShrink: 0,
          },
          {
            label: 'Auto',
            key: 'auto',
            cellDataGetter: ({
              rowData,
            }: {
              rowData: GetCampaignsQuery_campaigns_Campaign;
            }) => (
              <AutomationIcon
                automationTime={rowData.newsletter?.automationTime}
              />
            ),
            width: 70,
            flexShrink: 0,
          },
          {
            label: 'Accès',
            key: 'lock',
            cellDataGetter: ({
              rowData,
            }: {
              rowData: GetCampaignsQuery_campaigns_Campaign;
            }) =>
              rowData.lockedBy && (
                <LockButton
                  lockedBy={rowData.lockedBy}
                  onClick={(e) => {
                    e.preventDefault();
                    e.stopPropagation();
                    if (rowData.lockedBy?.id === auth.user?.id) {
                      openMenu(
                        e.currentTarget,
                        <MenuItem
                          onClick={() => {
                            updateLockerId(rowData.id, null);
                            closeMenu();
                          }}
                        >
                          Terminer l'édition
                        </MenuItem>,
                      );
                    }
                  }}
                  disableLockForV0
                />
              ),
            width: 120,
            flexShrink: 0,
          },
        ]}
      />
      <SpeedDialBrands brand={brandQueryParam} basePath={paths.CAMPAIGN_NEW} />
    </ListPage>
  );
};
